import { OESTRA_QUIZ_ENTRY_HREF } from "~/constants/oestraLandingPages";
import { createElement, Fragment } from "react";
import { createNavigationItem, createAuthNavigationItem } from "~/utils/navigation";
import { openChat } from "~/lib/intercom/IntercomScript";
import { ArticleLink, CardLinkData, ProductQuizLink, TreatmentLink } from "~/types/navigation";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import { APP_ROUTES } from "./appRoutes";
import { GTM_EVENT_NAMES } from "./gtmEvents";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";

export const HELP_CENTER_URL = "https://help.innerbalance.com/en/";
export const helpCenterArticleUrl = (slug: string) => `${HELP_CENTER_URL}articles/${slug}`;
export const helpCenterCollectionUrl = (slug: string) => `${HELP_CENTER_URL}collections/${slug}`;
// Outgoing-only line the care team calls patients from.
export const CARE_TEAM_CALL_PHONE = { TEL: "tel:+13233475288", DISPLAY: "(323) 347-5288" } as const;
export const APP_STORE_URL = "https://apps.apple.com/il/app/innerbalance-llc/id6749360971";

export const AUTH_PAGES = [
    createAuthNavigationItem("My Account", APP_ROUTES.HOME),
    createAuthNavigationItem("Chat Now", "#", openChat),
    createAuthNavigationItem("Supplements", "https://us.fullscript.com/welcome/innerbalancedrsarahdaccarett?utm_medium=webreferral&utm_source=other&utm_campaign=abmwebbuttons_light_200x200.svg&signup_source=website_buttons", undefined, "_blank"),
    createAuthNavigationItem("Help Center", HELP_CENTER_URL, undefined, "_blank"),
];

// MegaMenu Data for Header
export const SHOP_MEGAMENU_TREATMENT_LINKS: TreatmentLink[] = [
    createNavigationItem("General HRT", APP_ROUTES.TREATMENT_HRT),
    createNavigationItem("Endometriosis", APP_ROUTES.TREATMENT_ENDOMETRIOSIS),
    createNavigationItem("Perimenopause", APP_ROUTES.TREATMENT_PERIMENOPAUSE),
    createNavigationItem("Menopause", APP_ROUTES.TREATMENT_MENOPAUSE),
    createNavigationItem("PMOS/PCOS", APP_ROUTES.TREATMENT_PCOS),
    createNavigationItem("Postpartum", APP_ROUTES.TREATMENT_POSTPARTUM),
];

export const PRODUCT_OESTRA_QUIZ_LINKS: ProductQuizLink = {
    text: <TextWithRegisteredMark text="Oestra®" />,
    href: OESTRA_QUIZ_ENTRY_HREF,
    title: <TextWithRegisteredMark text="Oestra®" />,
    subtitle: "Restore balance with bioidentical HRT",
    image: {
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1771420585/new_design/image_3_tibkdg.webp",
        alt: "Inner Balance Oestra bioidentical hormone cream jar for menopause treatment",
    },
    eventData: {
        event: "user_interaction",
        value: {
            event_name: GTM_EVENT_NAMES.MAIN_NAVIGATION,
            widget: "sticky_header",
            meta_data: {
                click_text: "Oestra®",
            },
        },
    },
};

export const PRODUCT_LIBIDA_QUIZ_LINKS: ProductQuizLink = {
    text: <TextWithRegisteredMark text="Libida®" />,
    href: QUIZ_ROUTES.LIBIDA,
    title: <TextWithRegisteredMark text="Libida®" />,
    subtitle: "On-Demand Libido Booster",
    image: {
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1771420585/new_design/image_3_2_spal9i.webp",
        alt: "Inner Balance Libida on-demand libido booster tablets for women",
    },
    eventData: {
        event: "user_interaction",
        value: {
            event_name: GTM_EVENT_NAMES.MAIN_NAVIGATION,
            widget: "sticky_header",
            meta_data: {
                click_text: "Libida®",
            },
        },
    },
};

export const PRODUCT_NAD_QUIZ_LINKS: ProductQuizLink = {
    text: "NAD+",
    href: QUIZ_ROUTES.NAD,
    title: "NAD+",
    subtitle: "Longevity & Cellular Repair",
    image: {
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1771420585/new_design/image_3_1_ryumtp.webp",
        alt: "Inner Balance NAD+ longevity and cellular repair supplement capsules",
    },
    eventData: {
        event: "user_interaction",
        value: {
            event_name: GTM_EVENT_NAMES.MAIN_NAVIGATION,
            widget: "sticky_header",
            meta_data: {
                click_text: "NAD+",
            },
        },
    },
};

export const PRODUCT_BODYMATCHED_QUIZ_LINKS: ProductQuizLink = {
    text: <TextWithRegisteredMark text="BodyMatched® Anti-Aging Skincare" />,
    href: APP_ROUTES.PQP_ANTI_AGING_FACE_CREAM,
    title: <><TextWithRegisteredMark text="BodyMatched®" /> <br className="max-lg:hidden" />Anti-Aging Skincare</>,
    subtitle: "Skincare for skin health & unwanted facial hair",
    image: {
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1771420585/new_design/image_3_3_oek8b1.webp",
        alt: "Inner Balance BodyMatched anti-aging face cream for skin and hair",
    },
    eventData: {
        event: "user_interaction",
        value: {
            event_name: GTM_EVENT_NAMES.MAIN_NAVIGATION,
            widget: "sticky_header",
            meta_data: {
                click_text: "BodyMatched® Anti-Aging Skincare",
            },
        },
    },
};

// Named Shop Section Cards
export const HORMONAL_IMBALANCE_CARD: CardLinkData = {
    ...createNavigationItem("Hormonal Imbalance", APP_ROUTES.TREATMENT_HRT),
    title: "Hormonal Imbalance",
    subtitle: <TextWithRegisteredMark text="Oestra®" />,
    immediatelyShowIcon: true,
};

export const SEXUAL_WELLNESS_CARD: CardLinkData = {
    ...createNavigationItem("Sexual Wellness", APP_ROUTES.LIBIDA_LP),
    title: "Sexual Wellness",
    subtitle: <TextWithRegisteredMark text="Libida®" />,
    immediatelyShowIcon: true,
};

export const LONGEVITY_AND_CELLULAR_REPAIR_CARD: CardLinkData = {
    ...createNavigationItem("Longevity & Cellular Repair", "/p/longevity/nad"),
    title: "Longevity & Cellular Repair",
    subtitle: "NAD+",
    immediatelyShowIcon: true,
};

export const ANTI_AGING_SKINCARE_CARD: CardLinkData = {
    ...createNavigationItem("Anti-Aging Skin Care", APP_ROUTES.PQP_ANTI_AGING_FACE_CREAM),
    title: "Anti-Aging Skin Care",
    subtitle: "Face Cream",
    immediatelyShowIcon: true,
};

// Named Learn Section Cards
export const MEET_THE_DOCTOR_CARD: CardLinkData = {
    ...createNavigationItem("Meet the Doctor", "/about-us"),
    title: "Meet the Doctor",
    subtitle: "A message from Dr. Sarah Daccarett",
    image: {
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1764688184/new_design/f9ff348f7381a6db200f2fa3ec097c637af9a988_1_pgox66.webp",
        alt: "Dr. Sarah Daccarett MD, Inner Balance founder and CEO",
    },
};

export const OUR_SCIENCE_CARD: CardLinkData = {
    ...createNavigationItem("Our Approach", "/science"),
    title: "Our Approach",
    subtitle: "Science-backed evidence in our products",
    image: {
        src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1764688183/new_design/988d5484d9913cc45c8b8fb2d8d201ebef5625ce_1_owxzlv.webp",
        alt: "Test tube with liquid representing Inner Balance's science-backed hormone research",
    },
};

export const ARTICLE_LINKS: ArticleLink[] = [
    {
        text: "Starting Hormone Replacement Therapy for Perimenopause and Menopause",
        href: "/p/learn/20-starting-hormone-replacement-therapy-for-perimenopause-and-menopause-your-comprehensive-guide",
        title: createElement(Fragment, {}, "Starting Hormone Replacement Therapy for ", createElement("br", {}), "Perimenopause and Menopause"),
        subtitle: "10 min read",
        image: {
            src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1764688183/new_design/50f6cbd3269ebb74ec54d02bb8e212dcc8953587_1_nzyjqu.webp",
            alt: "Woman applying Oestra hormone cream to hand — HRT article",
        },
        eventData: {
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.MAIN_NAVIGATION,
                widget: "sticky_header",
                meta_data: {
                    click_text: "Starting Hormone Replacement Therapy for Perimenopause and Menopause",
                },
            },
        },
    },
    {
        text: "How Can Women Tell If Their Hormones Are Low or Unbalanced?",
        href: "/p/learn/22-ask-dr-sarah-how-can-women-tell-if-their-hormones-are-low-or-unbalanced",
        title: createElement(Fragment, {}, "How Can Women Tell If Their Hormones ", createElement("br", {}), "Are Low or Unbalanced?"),
        subtitle: "10 min read",
        image: {
            src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1764688183/new_design/1f5b26710b772012faa49bf74bd52972c922e979_1_ew06zl.webp",
            alt: "Oestra cream for women with low or unbalanced hormones",
        },
        eventData: {
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.MAIN_NAVIGATION,
                widget: "sticky_header",
                meta_data: {
                    click_text: "How Can Women Tell If Their Hormones Are Low or Unbalanced?",
                },
            },
        },
    },
    {
        text: "Why Doesn't Birth Control Work to Treat Endometriosis?",
        href: "/p/learn/ask-dr-sarah-why-doesnt-birth-control-work-to-treat-endometriosis-inner-balance",
        title: createElement(Fragment, {}, "Why Doesn't Birth Control Work to ", createElement("br", {}), "Treat Endometriosis?"),
        subtitle: "8 min read",
        image: {
            src: "https://res.cloudinary.com/dirscuuhp/image/upload/v1764688184/new_design/fc6866f724099b65a149bff503012e67ef244196_patnpa.webp",
            alt: "Woman with clear skin — birth control doesn't treat endometriosis article",
        },
        eventData: {
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.MAIN_NAVIGATION,
                widget: "sticky_header",
                meta_data: {
                    click_text: "Why Doesn't Birth Control Work to Treat Endometriosis?",
                },
            },
        },
    },
];

// Routes Configuration
// TODO: All spaces shoukld be configureed only by styles
/**
 * Routes that should not include navbar spacer
 * These pages handle their own spacing or have custom layout requirements
 */
export const ROUTES_WITHOUT_NAVBAR_SPACER: readonly string[] = [
    APP_ROUTES.ROOT,
    APP_ROUTES.HOMEPAGE,
    APP_ROUTES.REVIEWS,
    APP_ROUTES.ABOUT_US,
    APP_ROUTES.POST_WEBINAR,
    APP_ROUTES.PQP_HORMONE_IMBALANCE,
    APP_ROUTES.PQP_ENDOMETRIOSIS,
    APP_ROUTES.LIVE_INTRO_OFFICE_HOURS,
    APP_ROUTES.WEBINAR,
    APP_ROUTES.HOME,
    APP_ROUTES.THANK_YOU,
    APP_ROUTES.PQP_UPSELL_FACE_CREAM,
    APP_ROUTES.PQP_UPSELL_OESTRA_CREAM,
    APP_ROUTES.SCIENCE,
    APP_ROUTES.TREATMENT_MENOPAUSE,
    APP_ROUTES.TREATMENT_HRT_3,
    APP_ROUTES.TREATMENT_PCOS,
    APP_ROUTES.TREATMENT_POSTPARTUM,
    APP_ROUTES.OESTRA_REASONS,
    APP_ROUTES.TREATMENT_HRT,
    APP_ROUTES.TREATMENT_PERIMENOPAUSE,
    APP_ROUTES.BODYMATCHED_PERIMENOPAUSE_SKIN,
    APP_ROUTES.TREATMENT_PERIMENOPAUSE_AD,
    APP_ROUTES.TREATMENT_ENDOMETRIOSIS,
    APP_ROUTES.BODYMATCHED_7_REASONS,
    APP_ROUTES.LIBIDA_7_REASONS,
    APP_ROUTES.LIBIDA_LP,
    APP_ROUTES.LIBIDA_LP_PT141,
    APP_ROUTES.LIBIDA_LP_SCREAM,
    APP_ROUTES.LIBIDA_AD,
    APP_ROUTES.MENOPAUSE_AD,
    APP_ROUTES.FINASTERIDE,
    APP_ROUTES.BODYMATCHED_ESTRIOL,
    APP_ROUTES.BODYMATCHED_TRETINOIN,
    APP_ROUTES.NIACINAMIDE,
    APP_ROUTES.PERI_LABS,
    APP_ROUTES.MENOPAUSE_WEIGHT_GAIN,
    APP_ROUTES.TREATMENT_HRT_LIBIDO,
    APP_ROUTES.COMPARE_MIDI_HEALTH,
    APP_ROUTES.COMPARE_HONE_HEALTH,
    APP_ROUTES.COMPARE_ALLOY_HEALTH,
    APP_ROUTES.COMPARE_WINONA_HEALTH,
    APP_ROUTES.AMBASSADOR,
];
